import { useState, useCallback, useMemo } from 'react';
import { EditorApi, EditorProps } from '../components/Editor/types';
import { EditorEvents } from '@tiptap/react';

export type EditorReadyHandler = (api: EditorApi) => void;

export type EditorChangeHandler = (props: EditorEvents['update']) => void;

export interface UseEditorOptions {
  onEditorReady?: EditorReadyHandler;
  onEditorChange?: EditorChangeHandler;
  onFocus?: (ev: React.FocusEvent) => void;
  onBlur?: (ev: React.FocusEvent) => void;
  autoFocus?: boolean;
}

const useEditor = ({
  onEditorReady,
  onEditorChange,
  onFocus,
  onBlur,
  autoFocus,
}: UseEditorOptions = {}) => {
  const [editorApi, setEditorApi] = useState<EditorApi | null>(null);

  const handleEditorReady = useCallback<EditorReadyHandler>(
    api => {
      setEditorApi(api);
      onEditorReady && onEditorReady(api);
    },
    [onEditorReady]
  );

  const handleEditorChange = useCallback(
    props => {
      onEditorChange && onEditorChange(props);
    },
    [onEditorChange]
  );

  const editorProps = useMemo(
    () =>
      ({
        onReady: handleEditorReady,
        onChange: handleEditorChange,
        onFocus: onFocus,
        onBlur: onBlur,
        autoFocus,
      } as Partial<EditorProps>),
    [handleEditorReady, handleEditorChange, onFocus, onBlur, autoFocus]
  );

  return {
    editorApi,
    editorProps,
  };
};

export default useEditor;
